import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import dayOfYearPlugin from 'dayjs/plugin/dayOfYear';
import { FaCheck } from 'react-icons/fa';
import { FaCog } from 'react-icons/fa';  // icon for "Game Info" button
import ReactGA from 'react-ga4';
import html2canvas from 'html2canvas';

import Keypad from './Components/Keypad';
import './App.css';

// Extend dayjs
dayjs.extend(dayOfYearPlugin);

// GA
ReactGA.initialize('G-3J8GMVJ3X2');

// Define potential bonus points per phoneword
const POTENTIAL_BONUS = 20;

/** 
 * FREE MODE LEVELS
 */
const LEVELS_FREE = [
  { min: 0,   max: 25   },
  { min: 26,  max: 50   },
  { min: 51,  max: 75   },
  { min: 76,  max: 115  },
  { min: 116, max: 170  },
  { min: 171, max: 225  },
  { min: 226, max: 300  },
  { min: 301, max: 375  },
  { min: 376, max: 450  },
  { min: 451, max: 500  },
  { min: 501, max: 600  },
  { min: 601, max: 700  },
  { min: 701, max: Infinity }
];
function getFreeLevelIndex(score) {
  for (let i = 0; i < LEVELS_FREE.length; i++) {
    const { min, max } = LEVELS_FREE[i];
    if (score >= min && score <= max) return i;
  }
  return 0;
}

/** 
 * SEQUENTIAL => dot index
 */
function getCompDotIndex(scoreComp, maxPointsComp) {
  if (!maxPointsComp || maxPointsComp <= 0) return 0;
  const pct = (scoreComp / maxPointsComp) * 100;
  if (pct < 5)  return 0;
  if (pct < 10) return 1;
  if (pct < 20) return 2;
  if (pct < 30) return 3;
  if (pct < 40) return 4;
  if (pct < 50) return 5;
  if (pct < 60) return 6;
  if (pct < 70) return 7;
  if (pct < 80) return 8;
  if (pct < 85) return 9;
  if (pct < 90) return 10;
  if (pct < 95) return 11;
  return 12;
}

function getCompetitiveLevelName(scoreComp, maxPointsComp) {
  if (!maxPointsComp || maxPointsComp <= 0) return 'Level 1';
  const pct = (scoreComp / maxPointsComp) * 100;
  if (pct < 5)  return 'Level 1';
  if (pct < 10) return 'Level 2';
  if (pct < 20) return 'Level 3';
  if (pct < 30) return 'Level 4';
  if (pct < 40) return 'Level 5';
  if (pct < 50) return 'Level 6';
  if (pct < 60) return 'Level 7';
  if (pct < 70) return 'Level 8';
  if (pct < 80) return 'Level 9';
  if (pct < 85) return 'Level 10';
  if (pct < 90) return 'Level Silver';
  if (pct < 95) return 'Level Gold';
  return 'Level Platinum';
}

/** 
 * Decide if we are in FREE or COMP, 
 * then produce a name
 */
function getCurrentLevelName(gameMode, scoreFree, scoreComp, maxPointsComp) {
  if (gameMode === 'competitive') {
    return getCompetitiveLevelName(scoreComp, maxPointsComp);
  } else {
    const idx = getFreeLevelIndex(scoreFree);
    return `Level ${idx + 1}`;
  }
}

/** 
 * level name => accent color
 * Note: Level 2 color is set to #7d7d1d.
 */
function getAccentColor(levelName) {
  switch (levelName) {
    case 'Level 1':      return '#4CAF50';
    case 'Level 2':      return '#7d7d1d';
    case 'Level 3':      return '#FFA726';
    case 'Level 4':      return '#F44336';
    case 'Level 5':      return '#2196F3';
    case 'Level 6':      return '#9575CD';
    case 'Level 7':      return '#E040FB';
    case 'Level 8':      return '#EC407A';
    case 'Level 9':      return '#9C27B0';
    case 'Level 10':     return '#FF9800';
    case 'Level Silver': return '#C0C0C0';
    case 'Level Gold':   return '#FFD700';
    case 'Level Platinum': return '#E5E4E2';
    default:             return '#9575CD';
  }
}

/** letter->digit */
const letterToNumber = {
  a: '2', b: '2', c: '2',
  d: '3', e: '3', f: '3',
  g: '4', h: '4', i: '4',
  j: '5', k: '5', l: '5',
  m: '6', n: '6', o: '6',
  p: '7', q: '7', r: '7', s: '7',
  t: '8', u: '8', v: '8',
  w: '9', x: '9', y: '9', z: '9'
};

/** 
 * Hidden share preview => for capture 
 */
function SharePreview({ puzzleNumber, mode, levelName, scoreText, puzzleDigits }) {
  const phoneKeys = ['1','2','3','4','5','6','7','8','9','*','0','#'];
  return (
    <div
      id="share-preview"
      style={{
        width: '300px',
        height: '220px',
        backgroundColor: '#fff',
        border: '2px solid #888',
        padding: '1rem',
        boxSizing: 'border-box',
        fontFamily: 'sans-serif',
        fontSize: '0.8rem',
        position: 'absolute',
        left: '-9999px'
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '0.25rem', fontWeight: 'bold' }}>
        Phoneword.io — Puzzle #{puzzleNumber}
      </div>
      <div>{mode} Mode</div>
      <div>{levelName} — {scoreText}</div>

      <div style={{ marginTop: '0.5rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 30px)',
          gridGap: '4px',
          justifyContent: 'center'
        }}>
          {phoneKeys.map((digit, idx) => {
            if (digit === '*' || digit === '#') {
              return <div key={idx} style={{ visibility: 'hidden' }}>{digit}</div>;
            }
            const inPuzzle = puzzleDigits.includes(digit);
            const bg = inPuzzle ? '#4CAF50' : '#ccc';
            return (
              <div
                key={idx}
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  borderRadius: '50%'
                }}
              >
                {digit}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function App() {
  // Basic states
  const [dailyNumber, setDailyNumber] = useState(null);

  // free
  const [scoreFree, setScoreFree] = useState(0);
  const [foundWordsFree, setFoundWordsFree] = useState([]);
  const foundWordsFreeRef = useRef([]);  // Ref for free mode found words

  // comp
  const [scoreComp, setScoreComp] = useState(0);
  const [foundWordsComp, setFoundWordsComp] = useState([]);
  const foundWordsCompRef = useRef([]);  // Ref for competitive mode found words
  const [bonusTotalComp, setBonusTotalComp] = useState(0); // Track bonus for competitive mode

  // puzzle stats
  const [wordsCountFree, setWordsCountFree] = useState(0);
  const [wordsCountComp, setWordsCountComp] = useState(0);
  const [phoneWordsCountFree, setPhoneWordsCountFree] = useState(0);
  const [phoneWordsCountComp, setPhoneWordsCountComp] = useState(0);
  const [maxPointsComp, setMaxPointsComp] = useState(0);

  // puzzleNumber => day offset
  const [puzzleNumber, setPuzzleNumber] = useState(1);

  // Additional UI
  const [wordInput, setWordInput] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [fadeOut, setFadeOut] = useState(false);
  const [streak, setStreak] = useState(1);

  // "Streak + pick a mode" popup
  const [showStreakPopup, setShowStreakPopup] = useState(false);

  const [showAdModal, setShowAdModal] = useState(false);
  const [showHowToPlayModal, setShowHowToPlayModal] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const [usedDigits, setUsedDigits] = useState([]);
  const [gameMode, setGameMode] = useState(null);
  const [puzzleDigits, setPuzzleDigits] = useState([]);

  // references for level up
  const oldFreeLevelRef = useRef(getFreeLevelIndex(0));
  const oldCompLevelRef = useRef(getCompDotIndex(0, 100));
  const [showSeq100Modal, setShowSeq100Modal] = useState(false);

  // ephemeral level-up message and particles
  const [levelUpMessage, setLevelUpMessage] = useState('');
  const [particlesData, setParticlesData] = useState([]);

  // Check if on mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // NEW: Streak logic updated to only continue if a valid word was played yesterday.
  useEffect(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const storedDate = localStorage.getItem('localDate');
    const playedYesterday = localStorage.getItem('playedToday') === 'true';
    if (!storedDate || storedDate !== today) {
      const st = parseInt(localStorage.getItem('streak') || '0', 10);
      if (playedYesterday) {
        setStreak(st + 1);
        localStorage.setItem('streak', String(st + 1));
      } else {
        setStreak(0);
        localStorage.setItem('streak', '0');
      }
      localStorage.setItem('localDate', today);
      localStorage.setItem('foundWordsFree', JSON.stringify([]));
      localStorage.setItem('scoreFree', '0');
      localStorage.setItem('foundWordsComp', JSON.stringify([]));
      localStorage.setItem('scoreComp', '0');
      localStorage.removeItem('playedToday'); // clear flag for new day
      setShowStreakPopup(true);
    } else {
      const st2 = parseInt(localStorage.getItem('streak') || '0', 10);
      setStreak(st2);
      setShowStreakPopup(true);
    }
  }, []);

  // fetch puzzle
  useEffect(() => {
    const localDay = dayjs().format('YYYY-MM-DD');
    axios
      .get(`/api/daily-number?localDay=${localDay}`)
      .then((res) => {
        if (res.data) {
          const diff = dayjs(localDay).diff(dayjs('2025-01-25'), 'day');
          setPuzzleNumber(diff + 1);

          const dnum = (res.data.dailyNumber || '').replace(/-/g, '');
          setDailyNumber(res.data.dailyNumber || '');
          setPuzzleDigits(dnum.split('') || []);

          setWordsCountFree(res.data.wordsCountFree || 0);
          setWordsCountComp(res.data.wordsCountComp || 0);
          setPhoneWordsCountFree(res.data.phoneWordsCountFree || 0);
          setPhoneWordsCountComp(res.data.phoneWordsCountComp || 0);
          setMaxPointsComp(res.data.maxPointsComp || 0);
        }
      })
      .catch((err) => console.error('Error fetching daily puzzle:', err));
  }, []);

  // Determine effective maximum for competitive mode
  const effectiveMaxComp = maxPointsComp + (phoneWordsCountComp * POTENTIAL_BONUS);

  // user picks a mode => load local data
  const handleSelectMode = (mode) => {
    setGameMode(mode);
    setShowStreakPopup(false);
    setShowAdModal(true);

    if (mode === 'free') {
      const fw = localStorage.getItem('foundWordsFree');
      const sc = localStorage.getItem('scoreFree');
      const parsedFw = fw ? JSON.parse(fw) : [];
      setFoundWordsFree(parsedFw);
      foundWordsFreeRef.current = parsedFw;
      setScoreFree(sc ? parseInt(sc, 10) : 0);
    } else {
      const fw = localStorage.getItem('foundWordsComp');
      const sc = localStorage.getItem('scoreComp');
      const parsedFw = fw ? JSON.parse(fw) : [];
      setFoundWordsComp(parsedFw);
      foundWordsCompRef.current = parsedFw;
      setScoreComp(sc ? parseInt(sc, 10) : 0);
      setBonusTotalComp(0); // reset competitive bonus on mode selection
    }
  };

  // NEW: Trigger Google Ad load when the ad modal is shown.
  useEffect(() => {
    if (showAdModal) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error("Adsbygoogle push error:", e);
      }
    }
  }, [showAdModal]);

  // persist found words
  useEffect(() => {
    if (!gameMode) return;
    if (gameMode === 'free') {
      localStorage.setItem('foundWordsFree', JSON.stringify(foundWordsFree));
      localStorage.setItem('scoreFree', String(scoreFree));
    } else {
      localStorage.setItem('foundWordsComp', JSON.stringify(foundWordsComp));
      localStorage.setItem('scoreComp', String(scoreComp));
    }
  }, [foundWordsFree, scoreFree, foundWordsComp, scoreComp, gameMode]);

  // track used digits
  useEffect(() => {
    if (!dailyNumber) return;
    const digits = dailyNumber.replace(/-/g, '').split('');
    const usage = {};
    digits.forEach(d => {
      usage[d] = (usage[d] || 0) + 1;
    });
    const used = [];
    for (const ch of wordInput.toLowerCase()) {
      const mapped = letterToNumber[ch];
      if (mapped && usage[mapped] > 0) {
        used.push(mapped);
        usage[mapped]--;
      }
    }
    setUsedDigits(used);
  }, [wordInput, dailyNumber]);

  // fade out message
  useEffect(() => {
    if (message.text) {
      setFadeOut(false);
      const fadeT = setTimeout(() => setFadeOut(true), 2500);
      const removeT = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
      return () => {
        clearTimeout(fadeT);
        clearTimeout(removeT);
      };
    }
  }, [message]);

  // each time score changes => check level up or 100%
  useEffect(() => {
    if (!gameMode) return;
    const lvlName = getCurrentLevelName(gameMode, scoreFree, scoreComp, maxPointsComp);

    if (gameMode === 'free') {
      const oldIndex = oldFreeLevelRef.current;
      const newIndex = getFreeLevelIndex(scoreFree);
      if (newIndex > oldIndex) {
        handleLevelUp(lvlName);
        oldFreeLevelRef.current = newIndex;
      }
    } else {
      const oldIndex = oldCompLevelRef.current;
      const newIndex = getCompDotIndex(scoreComp, maxPointsComp);
      if (newIndex > oldIndex) {
        handleLevelUp(lvlName);
        oldCompLevelRef.current = newIndex;
      }
      // 100% check for competitive mode now uses effective maximum (base + potential bonus if phonewords exist)
      if (maxPointsComp > 0 && scoreComp >= effectiveMaxComp) {
        setShowSeq100Modal(true);
      }
    }
  }, [scoreFree, scoreComp, gameMode, maxPointsComp, effectiveMaxComp]);

  // spawn ephemeral message + 30 random-floating particles
  function handleLevelUp(lvlName) {
    setLevelUpMessage(`You reached ${lvlName}!`);

    const newParticles = [...Array(30)].map(() => ({
      id: Math.random().toString(36).slice(2),
      left: Math.random() * 100,
      delay: Math.random() * 1.5
    }));
    setParticlesData(newParticles);

    setTimeout(() => {
      setLevelUpMessage('');
      setParticlesData([]);
    }, 3000);
  }

  function handleWordSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!dailyNumber) {
      setMessage({ text: 'Daily puzzle not loaded yet.', type: 'error' });
      return;
    }
    if (!gameMode) {
      setMessage({ text: 'Please select a mode first.', type: 'error' });
      return;
    }

    const word = wordInput.trim().toLowerCase();
    if (word.length < 3) {
      setMessage({ text: 'Words must be at least 3 letters.', type: 'error' });
      return;
    }

    // Use the appropriate ref to check for duplicates
    const currentFoundWordsRef = gameMode === 'free' ? foundWordsFreeRef : foundWordsCompRef;
    if (currentFoundWordsRef.current.includes(word)) {
      setMessage({ text: 'You already found that word.', type: 'error' });
      return;
    }

    axios.post('/api/check-word', { word, dailyNumber, mode: gameMode })
      .then(res => {
        if (res.data.valid) {
          const length = word.length;
          const digitsInWord = word.split('').map(ch => letterToNumber[ch]);
          const usedAll = (
            digitsInWord.length === 7 &&
            digitsInWord.every(d => dailyNumber.replace(/-/g, '').includes(d))
          );

          let bonus = 0;
          if (usedAll) {
            bonus += 20;
            if (gameMode === 'competitive') {
              setBonusTotalComp(prev => prev + 20);
            }
            setMessage({ text: `PhoneWord! +${length + bonus} points.`, type: 'success' });
          } else {
            setMessage({ text: `Valid word! +${length} points.`, type: 'success' });
          }

          if (gameMode === 'free') {
            setFoundWordsFree(prev => {
              const newArr = [...prev, word];
              foundWordsFreeRef.current = newArr;
              return newArr;
            });
            setScoreFree(prev => prev + length + bonus);
          } else {
            setFoundWordsComp(prev => {
              const newArr = [...prev, word];
              foundWordsCompRef.current = newArr;
              return newArr;
            });
            setScoreComp(prev => prev + length + bonus);
          }
          // Mark that a valid word was played today.
          localStorage.setItem('playedToday', 'true');

          ReactGA.event({
            category: 'WordSubmission',
            action: 'SubmitWord',
            label: word.toUpperCase(),
            value: length + bonus
          });
        } else {
          setMessage({ text: `Invalid word: ${res.data.reason || 'Unknown'}`, type: 'error' });
        }
      })
      .catch(err => {
        console.error('Error validating word:', err);
        setMessage({ text: 'Server error validating word.', type: 'error' });
      })
      .finally(() => {
        setWordInput('');
        setUsedDigits([]);
        window.scrollTo(0, 0);
      });
  }

  // top bar => level name
  const levelName = getCurrentLevelName(gameMode, scoreFree, scoreComp, maxPointsComp);

  // accent color
  useEffect(() => {
    const accent = getAccentColor(levelName);
    document.documentElement.style.setProperty('--accent-color', accent);
  }, [levelName]);

  // share
  async function handleShare() {
    const shareElement = document.getElementById('share-preview');
    if (!shareElement) return;

    try {
      const canvas = await html2canvas(shareElement, { scale: 2 });
      const dataURL = canvas.toDataURL('image/png');
      const blob = await (await fetch(dataURL)).blob();
      const file = new File([blob], 'phoneword-share.png', { type: 'image/png' });

      let shareText;
      if (gameMode === 'competitive') {
        shareText = `I just scored ${scoreComp}/${effectiveMaxComp} in Sequential Mode on Phoneword.io Puzzle #${puzzleNumber}.

Give it a try! https://phoneword.io`;
      } else {
        shareText = `I just scored ${scoreFree} in Free Mode on Phoneword.io Puzzle #${puzzleNumber}.
Give it a try! https://phoneword.io`;
      }

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          text: shareText,
          title: 'My Phoneword.io Score'
        });
      } else {
        const link = document.createElement('a');
        link.download = 'phoneword-share.png';
        link.href = dataURL;
        link.click();
      }
    } catch (err) {
      console.error('Error generating share image:', err);
    }
  }

  // found words
  const currentFoundWords = (gameMode === 'free') ? foundWordsFree : foundWordsComp;
  const hasWords = currentFoundWords.length > 0;
  const lastFourWords = currentFoundWords.slice(-4);

  const sortedWords = [...currentFoundWords].sort((a, b) => a.localeCompare(b));
  const perColumn = Math.ceil(sortedWords.length / 3);
  const col1 = sortedWords.slice(0, perColumn);
  const col2 = sortedWords.slice(perColumn, perColumn * 2);
  const col3 = sortedWords.slice(perColumn * 2);

  const totalPossibleWords = (gameMode === 'competitive') ? wordsCountComp : wordsCountFree;
  const totalPossiblePhoneWords = (gameMode === 'competitive') ? phoneWordsCountComp : wordsCountFree;

  // A function to re-open the mode/instructions popup
  function handleShowGameModal() {
    setShowStreakPopup(true);
  }

  return (
    <div className="App">
      {/* Share preview offscreen */}
      <SharePreview
        puzzleNumber={puzzleNumber}
        mode={gameMode === 'competitive' ? 'Sequential' : 'Free'}
        levelName={levelName}
        scoreText={
          gameMode === 'competitive'
            ? `${scoreComp}/${effectiveMaxComp}`
            : `${scoreFree} pts`
        }
        puzzleDigits={puzzleDigits}
      />

      {/* 30 random-floating particles if needed */}
      {particlesData.length > 0 && (
        <div className="particle-container">
          {particlesData.map((p) => {
            const style = {
              left: `${p.left}vw`,
              animationDelay: `${p.delay}s`
            };
            return <div key={p.id} className="floating-particle" style={style} />;
          })}
        </div>
      )}

      {/* ephemeral “You reached Level X!” */}
      {levelUpMessage && (
        <div className="level-up-overlay">
          {levelUpMessage}
        </div>
      )}

      {/* 100% in sequential => special modal */}
      {showSeq100Modal && (
        <div className="modal-backdrop">
          <div className="modal celebration-modal">
            <h2>All Points Found!</h2>
            <p>Congratulations! You reached 100% in Sequential Mode.</p>
            <button onClick={handleShare}>Share</button>
            <button onClick={() => setShowSeq100Modal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* top bar => level left, puzzle # center, score right */}
      <div className="top-bar">
        <div className="left-level">
          <span className="level-text">{levelName}</span>
        </div>
        <h1 className="small-title center-title">
          Phoneword.io Puzzle #{puzzleNumber}
        </h1>
        <div className="right-score">
          {gameMode === 'competitive'
            ? `Score: ${scoreComp}/${effectiveMaxComp}`
            : `Score: ${scoreFree}`
          }
        </div>
      </div>

      {/* Streak & Mode selection popup */}
      {showStreakPopup && (
        <div className="modal-backdrop">
          <div className="modal">
            <p>You have a {streak}-day streak!</p>
            <p>Select a game mode:</p>
            <button onClick={() => handleSelectMode('free')} style={{ marginRight: '0.5rem' }}>
              Free Mode
            </button>
            <button onClick={() => handleSelectMode('competitive')}>Sequential Mode</button>
            <div style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
              <button onClick={() => setShowHowToPlayModal(true)}>How to Play?</button>
            </div>
          </div>
        </div>
      )}

      {/* Ad modal */}
      {showAdModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ width: '90%', maxWidth: '600px', padding: '2rem', textAlign: 'center' }}>
            <p>Advertisement</p>
            <ins
              className="adsbygoogle"
              style={{ display: 'block', width: '100%', height: '90px', margin: '1rem 0' }}
              data-ad-client="ca-pub-2125123314786497"
              data-ad-slot="8320264171"
              data-ad-format="auto"
              data-full-width-responsive="true"
            />
            <button onClick={() => setShowAdModal(false)}>Continue</button>
          </div>
        </div>
      )}

      {/* How to Play => RESTORE your instructions */}
      {showHowToPlayModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <h2>How to Play</h2>
            <div className="how-to-play-body">
              <p><strong>General</strong></p>
              <ul>
                <li>Phoneword is an anagram game based on phone numbers.</li> 
                <li>Discover as many words as possible by using the letters corresponding 
                  to the available numbers in that day's phone number.</li>
                <li>Each word is worth points based on its length.</li>
                <li>Words must be at least 3 letters. 
                  A 7-letter “PhoneWord” yields a big bonus!</li>
              </ul>

              <p><strong>Free Mode</strong></p>
              <ul>
                <li>You can form words by using the available digits in 
                  any order, as long as each digit is available.</li>
                <li>For example, if the daily number is 746-7263, then "FARMS" is a valid
                  word because it corresponds to the digits 3-2-7-6-7 which are all available in the number. The word "RAMPS" is NOT valid because it would require "7-2-6-7-7" and there are only two 7s available. 
                </li>
              </ul>

              <p><strong>Sequential Mode</strong></p>
              <ul>
                <li>Letters must map to consecutive digits 
                  in the phone number (e.g., 3-6-2 in order).</li>
                <li>For example, if the daily number is 746-7263, then "FARMS" is NOT a valid word 
                  because the digits 3-2-7-6-7 are not consecutive in the number. The word "PIN" would be a valid word because it corresponds to "7-4-6" which appears in the daily number in that order.</li>
                <li>Achieve 100% of possible points
                  to become a Phoneword champion!</li>
              </ul>
            </div>
            <button onClick={() => setShowHowToPlayModal(false)}>Close</button>
            <hr></hr>
            <p>Thank you for playing Phoneword!</p>
            <hr></hr>
          </div>
        </div>
      )}

      {/* Daily phone number */}
      {dailyNumber ? (
        <h2 className="daily-number">
          <span className="small-dial">1-800-</span>
          {dailyNumber}
        </h2>
      ) : (
        <p>Loading daily puzzle...</p>
      )}

      {/* 2-column => dots left, keypad right */}
      <div className="two-column-layout">
        <div className="level-dots-container">
          {[...LEVELS_FREE].reverse().map((_, reversedIndex) => {
            const actualIndex = LEVELS_FREE.length - 1 - reversedIndex;
            const dotIndex = (gameMode === 'competitive')
              ? getCompDotIndex(scoreComp, maxPointsComp)
              : getFreeLevelIndex(scoreFree);
            const isActive = (actualIndex === dotIndex);
            return (
              <div
                key={reversedIndex}
                className={`level-dot ${isActive ? 'active-dot' : ''}`}
              />
            );
          })}
        </div>
        <div className="keypad-container">
          <Keypad dailyNumber={dailyNumber || ''} usedDigits={usedDigits} />
        </div>
      </div>

      {/* message container */}
      <div className="message-container">
        {message.text && (
          <p className={`message ${message.type} ${fadeOut ? 'fade-out' : ''}`}>
            {message.text}
          </p>
        )}
      </div>

      {/* word input */}
      <div className="bottom-row">
        <form onSubmit={handleWordSubmit} className="word-form">
          <input
            type="text"
            value={wordInput}
            onChange={(e) => setWordInput(e.target.value)}
            placeholder="Enter a word"
            className="word-input"
            readOnly={isMobile}
          />
          <button type="submit" className="circle-submit-btn">
            <FaCheck />
          </button>
        </form>
      </div>

      {/* QWERTY Keyboard */}
      <QwertyKeyboard
        onLetter={(letter) => setWordInput(prev => prev + letter.toLowerCase())}
        onBackspace={() => setWordInput(prev => prev.slice(0, -1))}
        onEnter={() => handleWordSubmit({ preventDefault: () => {} })}
      />

      {/* Found words => collapsed => expanded */}
      {((gameMode === 'free') ? foundWordsFree.length : foundWordsComp.length) > 0 && (
        <div className="found-words-container">
          {!expanded && (
            <div className="collapsed-row">
              <div className="collapsed-inner">
                <ul className="collapsed-words-list">
                  {lastFourWords.map((w, i) => (
                    <li key={i} className="tiny-pill">
                      {w.toUpperCase()}
                    </li>
                  ))}
                </ul>
                <span className="expand-arrow" onClick={() => setExpanded(true)}>
                  ▼
                </span>
              </div>
            </div>
          )}
          {expanded && (
            <div className="expanded-overlay">
              <div className="overlay-header">
                <span className="collapse-arrow" onClick={() => setExpanded(false)}>
                  ▲
                </span>
              </div>
              <div className="words-columns">
                <ul>
                  {col1.map((word, idx) => (
                    <li key={`col1-${idx}`} className="pill">
                      {word.toUpperCase()}
                    </li>
                  ))}
                </ul>
                <ul>
                  {col2.map((word, idx) => (
                    <li key={`col2-${idx}`} className="pill">
                      {word.toUpperCase()}
                    </li>
                  ))}
                </ul>
                <ul>
                  {col3.map((word, idx) => (
                    <li key={`col3-${idx}`} className="pill">
                      {word.toUpperCase()}
                    </li>
                  ))}
                </ul>
                <div className="expanded-info">
                  <p>
                    <strong>Total Possible Words:</strong> {(gameMode === 'competitive') ? wordsCountComp : wordsCountFree}
                  </p>
                  <p>
                    <strong>Total Possible PhoneWords:</strong> {(gameMode === 'competitive') ? phoneWordsCountComp : phoneWordsCountFree}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BOTTOM buttons => Share & "Game Info" */}
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
        <button
          style={{
            backgroundColor: 'var(--accent-color)',
            color: '#fff',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
          onClick={handleShare}
        >
          Share
        </button>

        <button
          style={{
            backgroundColor: 'var(--accent-color)',
            color: '#fff',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem'
          }}
          onClick={() => setShowStreakPopup(true)}
        >
          <FaCog /> Game Info
        </button>
      </div>

      <footer className="footer" style={{ marginTop: '1rem' }}>
        © 2025 Odacova Games
      </footer>
    </div>
  );
}

/** QWERTY Keyboard */
function QwertyKeyboard({ onLetter, onBackspace, onEnter }) {
  const rows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACK']
  ];

  const handleKeyPress = (key) => {
    if (key === 'BACK') {
      onBackspace();
    } else {
      onLetter(key);
    }
  };

  return (
    <div className="qwerty-keyboard">
      {rows.map((row, rowIndex) => (
        <div className="qwerty-row" key={rowIndex}>
          {row.map((keyValue) => (
            <button
              type="button"
              className="keycap"
              key={keyValue}
              onClick={() => handleKeyPress(keyValue)}
            >
              {keyValue}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

export default App;